<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    width="1000"
  >
    <v-card>
      <v-card-title primary-title>
        Upload a photos
        <div class="text-center">
          <v-dialog
            v-model="photoRequirementsDialog"
            width="500"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                class="ml-2"
                small
                text
                v-bind="attrs"
                v-on="on"
                @click="photoRequirementsDialog = true"
              >
                (See requirements)
              </v-btn>
            </template>

            <v-card>
              <v-card-title class="text-h6 grey lighten-2">
                Passport photo requirements
              </v-card-title>

              <v-card-text>
                <ul>
                  <li>high resolution photo that is not blurry, grainy, or pixelated.</li>
                  <li>2 x 2 inches (51 x 51 mm)</li>
                  <li>
                    Head must be between 1 -1 3/8 inches (25 - 35 mm) from the bottom of the chin to the top of the
                    head
                  </li>
                  <li>Printed on matte or glossy photo quality paper</li>
                  <p>
                    We suggest you to use <a
                      href="https://tsg.phototool.state.gov/photo"
                      target="_blank"
                    >this tool</a>
                    to make sure your photo is passport compliant.
                  </p>
                </ul>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  text
                  @click="photoRequirementsDialog = false"
                >
                  Dismiss
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </v-card-title>

      <v-card-text>
        <v-row
          v-if="pePhotoLoading"
          justify="center"
          align="center"
          class="py-12"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
          />
          <span class="ml-4 text-subtitle-1">Loading photo...</span>
        </v-row>

        <v-row v-if="hadPhoto && userCan.downloadPartyPhoto">
          <v-col>
            <v-row
              justify="center"
              align="center"
            >
              <v-col
                cols="12"
                md="6"
                class="d-flex flex-column align-center justify-center"
              >
                <p class="text-subtitle-1 font-weight-bold mb-2">
                  Current photo
                </p>
                <v-img
                  class="photo-preview rounded-lg"
                  contain
                  height="400"
                  width="400"
                  :src="peGetPhotoDataUrl"
                />
              </v-col>

              <v-col
                cols="12"
                md="4"
                class="d-flex flex-column justify-center pl-md-6"
              >
                <v-btn
                  color="primary"
                  large
                  class="mb-3 justify-start"
                  block
                  :disabled="editing"
                  @click="_ => setEditingMode(true)"
                >
                  <v-icon left>
                    mdi-image-edit
                  </v-icon>
                  {{ editing ? 'Editing Active...' : 'Edit Photo' }}
                </v-btn>

                <v-menu
                  offset-y
                  block
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      dark
                      large
                      class="mb-3 justify-start"
                      block
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left>
                        mdi-download
                      </v-icon>
                      Download As...
                      <v-spacer />

                      <v-icon
                        small
                        right
                      >
                        mdi-chevron-down
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="_ => onPrintPhotoToPdf('8.5x11')">
                      <v-list-item-title>PDF Layout (2 pictures) (8.5" x 11")</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="_ => onPrintPhotoToPdf('4x6')">
                      <v-list-item-title>PDF Layout (2 pictures) (4" x 6")</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="_ => onDownloadPhoto(peParty.photo)">
                      <v-list-item-title>Single Crop JPG</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-menu offset-y>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      dark
                      large
                      class="mb-3 justify-start"
                      block
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left>
                        mdi-printer
                      </v-icon>
                      Print As...
                      <v-spacer />

                      <v-icon
                        small
                        right
                      >
                        mdi-chevron-down
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="_ => onPrintPhotoToPrinter('8.5x11')">
                      <v-list-item-title>Print Layout (8.5" x 11")</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="_ => onPrintPhotoToPrinter('4x6')">
                      <v-list-item-title>Print Layout (4" x 6")</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-btn
                  color="secondary"
                  outlined
                  large
                  block
                  class="justify-start"
                  @click="_ => onDownloadPhoto(peParty.photo_original)"
                >
                  <v-icon left>
                    mdi-history
                  </v-icon>
                  Download Original
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider
          v-if="hadPhoto && editing"
          class="my-6"
        />

        <v-row v-if="editing">
          <v-col>
            <v-row>
              <v-col>
                <p class="font-weight-bold text-overline">
                  Please select photo
                </p>
                <v-file-input
                  v-model="selectedPhoto"
                  label="Photo Select"
                  filled
                  accept="image/*"
                  prepend-icon="mdi-camera"
                  :loading="convertingHeic"
                  :disabled="convertingHeic"
                  @change="_ => onFileChange()"
                />
              </v-col>
            </v-row>

            <v-row v-if="image.src && userCan.uploadPartyPhoto">
              <v-col
                cols="12"
                md="6"
              >
                <span class="text-overline text-center font-weight-bold d-block mb-2">Original Photo</span>
                <v-card
                  flat
                  class="cropper-container"
                  :class="{ 'has-transparent-bg': backgroundRemoved }"
                  :style="cropperBackgroundStyle"
                >
                  <Cropper
                    ref="cropper"
                    class="cropper"
                    :src="image.src"
                    :stencil-props="{
                      aspectRatio: 1,
                      handlersClasses: {
                        default: 'custom-handler'
                      },
                      linesClasses: {
                        default: 'custom-line'
                      }
                    }"
                    @change="event => onCropperChange(event)"
                    @ready="_ => onCropperReady()"
                  />
                </v-card>
                <div class="d-flex justify-center mt-3 text-overline grey--text text--darken-1 min-h-36">
                  <v-btn
                    v-if="!removingBackground && !backgroundRemoved"
                    color="secondary"
                    outlined
                    @click="onRemoveBackground"
                  >
                    <v-icon left>
                      mdi-image-filter-vintage
                    </v-icon>
                    Remove Background
                  </v-btn>

                  <span
                    v-else-if="removingBackground"
                    class="font-weight-bold primary--text animate-pulse"
                  >
                    {{ caption }}
                  </span>
                </div>
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-row>
                  <v-col>
                    <span class="text-overline text-center font-weight-bold d-block mb-2">Passport Photo Preview</span>
                    <div class="preview-wrapper">
                      <v-img
                        class="photo-preview"
                        contain
                        height="400"
                        width="400"
                        :src="resultImg"
                      />
                      <v-img
                        class="preview-guide"
                        height="400"
                        width="400"
                        src="/images/passport-photo-guide.svg"
                      />
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-btn
          v-if="hadPhoto"
          color="error"
          text
          :disabled="uploading"
          @click="onFileDelete"
        >
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="secondary"
          text
          :disabled="uploading"
          @click="onDialogClose"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          text
          :loading="uploading"
          :disabled="!selectedPhoto"
          @click="_ => onFileUpload()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import 'vue-advanced-cropper/dist/style.css'
import { mapActions, mapGetters } from 'vuex'
import { Cropper } from 'vue-advanced-cropper'
// Import heic2any for client-side decoding
import heic2any from 'heic2any'
import { removeBackground } from '@imgly/background-removal'

import { downloadURI, getMimeType } from '@/utilities'
import actions from '@/data/Permissions/enums.json'
import { alertHandler } from '@/alertHandler'
import { axiosRequest } from '@/plugins/axios-common'
import { parseErrorResponseMessage } from '@/utilities/error-responses'

import { PARTY_NAMESPACE } from '@/store/party'
import { RESOURCETYPES } from '@/store/constants'
import { SHARED_NAMESPACE } from '@/store/shared'
import { USER_NAMESPACE } from '@/store/user'

export default {
  name: 'PEPartyPhotoDialog',

  components: {
    Cropper,
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      backgroundRemoved: false,
      caption: '',
      convertingHeic: false,
      editing: false,
      hadPhoto: false,

      image: {
        src: null,
        type: null,
      },

      pendingCropCoordinates: null,
      pendingCropImageSize: null,
      photoRequirementsDialog: false,
      removingBackground: false,
      resultImgLocal: '',
      selectedPhoto: null,
      uploading: false, // Loading spinner hook for UI feedback
    }
  },

  computed: {
    cropperBackgroundStyle () {
      if (!this.image?.src) return {}
      // Force continuous fallback canvas wrappers to hold white backgrounds instead of transparency drops
      return { '--cropper-current-bg': '#FFFFFF' }
    },

    dialog: {
      get () { return this.value },

      set (val) {
        this.$emit('input', val)
      },
    },

    resultImg: {
      get () { return this.selectedPhoto ? this.resultImgLocal : this.image?.src },
      set (val) { this.resultImgLocal = val },
    },

    userCan () {
      return {
        downloadPartyPhoto: this.canToResource(
          actions.Party.DownloadPhoto,
          RESOURCETYPES.PARTIES,
          this.peParty?.id
        ),

        uploadPartyPhoto: this.canToResource(
          actions.Party.Upload,
          RESOURCETYPES.PARTIES,
          this.peParty?.id
        ),
      }
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peGetPhotoDataUrl',
      'peGetPhotoOriginalBlob',
      'peGetPhotoOriginalDataUrl',
      'peParty',
      'pePhotoLoading',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'canToResource',
    ]),
  },

  watch: {
    // Photo may still be loading (async fetch) when the dialog is opened right after
    // party edit is triggered — retry once loading finishes instead of staying stuck
    // showing the "no photo" upload state for the lifetime of this dialog instance.
    pePhotoLoading (loading) {
      if (!loading && this.value) {
        this.checkIfHasPhoto()
      }
    },

    value (val) {
      if (val) {
        this.checkIfHasPhoto()
      }
    },
  },

  destroyed () {
    this.clearCache()
  },

  methods: {
    checkIfHasPhoto () {
      // Photo blobs are still being fetched — the `pePhotoLoading` watcher will
      // re-run this once they're ready, so don't jump to "no photo" yet.
      if (this.pePhotoLoading) return

      this.clearCache()
      if (this.peGetPhotoOriginalDataUrl && this.peGetPhotoOriginalBlob) {
        const blobUrl = URL.createObjectURL(this.peGetPhotoOriginalBlob)

        this.image = {
          src: blobUrl,
          type: this.peGetPhotoOriginalBlob.type || 'image/jpeg',
        }

        this.selectedPhoto = new File(
          [this.peGetPhotoOriginalBlob],
          'photo_original.jpg',
          { type: this.image.type }
        )

        this.hadPhoto = true
        this.editing = false
      } else {
        this.hadPhoto = false
        this.setEditingMode(true)
      }
    },

    clearCache () {
      if (this.image.src && this.image.src.startsWith('blob:')) {
        URL.revokeObjectURL(this.image.src)
      }
      this.setEditingMode(false)
      this.resultImgLocal = ''
      this.selectedPhoto = null
      this.image.src = null
      this.removingBackground = false
      this.backgroundRemoved = false
      this.uploading = false
      this.convertingHeic = false
      this.pendingCropCoordinates = null
      this.pendingCropImageSize = null
    },

    /**
     * Converts a HEIC/HEIF file to JPEG via three cascading paths:
     *   1. <img> → canvas: Chrome on macOS routes through CoreGraphics, handles HDR HEIC
     *   2. createImageBitmap → canvas: Chrome 129+ native HEIC decoder
     *   3. heic2any WASM: JS fallback for standard HEIC on other browsers
     * Always resolves to a JPEG File so downstream code never sees HEIC bytes.
     */
    async convertHeicToJpeg (file) {
      const outName = file.name.replace(/\.[^.]+$/, '') + '.jpg'

      const blobToCanvas = source => new Promise((resolve, reject) => {
        const w = source.naturalWidth || source.width
        const h = source.naturalHeight || source.height
        if (!w || !h) { reject(new Error('zero-dimension source — decoder did not populate dimensions')); return }
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, w, h)
        ctx.drawImage(source, 0, 0)
        canvas.toBlob(blob => {
          if (!blob) { reject(new Error('toBlob returned null')); return }
          resolve(new File([blob], outName, { type: 'image/jpeg' }))
        }, 'image/jpeg', 0.95)
      })

      // Path 1: Server-side — handles all HEIC variants via sips/libheif/ffmpeg/vips
      try {
        const formData = new FormData()
        formData.append('file', file, file.name)
        const response = await axiosRequest.post('/image/heic_convert', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'blob',
        })
        return new File([response.data], outName, { type: 'image/jpeg' })
      } catch (serverErr) {
        // console.warn('[HEIC] Server conversion failed — status:', serverErr?.response?.status, serverErr?.message)
      }

      // Path 2: <img> element — uses OS decoder (CoreGraphics on macOS), handles HDR HEIC
      try {
        const url = URL.createObjectURL(file)
        const img = await new Promise((resolve, reject) => {
          const el = new Image()
          el.onload = _ => { URL.revokeObjectURL(url); resolve(el) }
          el.onerror = _ => { URL.revokeObjectURL(url); reject(new Error('img load failed')) }
          el.src = url
        })
        return await blobToCanvas(img)
      } catch (_) {}

      // Path 3: createImageBitmap — Chrome 129+ built-in HEIC support
      try {
        const bitmap = await createImageBitmap(file)
        const jpeg = await blobToCanvas(bitmap)
        bitmap.close()
        return jpeg
      } catch (_) {}

      // Path 4: heic2any WASM — returns array for multi-frame files (Live Photos, burst)
      const result = await heic2any({ blob: file, quality: 0.95, toType: 'image/jpeg' })
      const blob = Array.isArray(result) ? result[0] : result
      return new File([blob], outName, { type: 'image/jpeg' })
    },

    /**
     * Prevents transparent pixel conversion drops from resolving to black profiles.
     * Takes an existing image canvas substrate, draws a solid #FFFFFF background first,
     * and layers the transparent image on top.
     */
    getFlattenedWhiteCanvas (sourceCanvas) {
      if (!sourceCanvas) return null

      const flattenedCanvas = document.createElement('canvas')
      flattenedCanvas.width = sourceCanvas.width
      flattenedCanvas.height = sourceCanvas.height

      const ctx = flattenedCanvas.getContext('2d')

      // Paint absolute white layout first
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, flattenedCanvas.width, flattenedCanvas.height)

      // Draw original image details over white canvas layout
      ctx.drawImage(sourceCanvas, 0, 0)
      return flattenedCanvas
    },

    onCropperChange ({ canvas }) {
      if (canvas) {
        const whiteCanvas = this.getFlattenedWhiteCanvas(canvas)
        this.resultImg = whiteCanvas.toDataURL('image/jpeg', 0.9)
      }
    },

    /**
     * Restores the crop selection after the cropper loads a new image (fired by
     * @ready, which lands after the cropper's own default-box reset has already
     * run — see onRemoveBackground). No-op on every other image load, since
     * pendingCropCoordinates is only ever set right before swapping in the
     * background-removed image.
     */
    onCropperReady () {
      if (!this.pendingCropCoordinates || !this.$refs.cropper) return

      const target = this.pendingCropCoordinates
      const previousImageSize = this.pendingCropImageSize
      this.pendingCropCoordinates = null
      this.pendingCropImageSize = null

      // NOTE: vue-advanced-cropper invokes a function-style transform with a single
      // { coordinates, imageSize, visibleArea } object, not positional (coordinates, imageSize)
      // args — passing the wrong shape here throws inside the library's transform loop and
      // silently no-ops the whole call (the exception is swallowed inside its $nextTick).
      this.$refs.cropper.setCoordinates(({ imageSize }) => {
        // The background-removed image is normally the same pixel size as the
        // original, but scale defensively in case it ever comes back resized.
        const scaleX = previousImageSize?.width ? imageSize.width / previousImageSize.width : 1
        const scaleY = previousImageSize?.height ? imageSize.height / previousImageSize.height : 1

        return {
          height: target.height * scaleY,
          left: target.left * scaleX,
          top: target.top * scaleY,
          width: target.width * scaleX,
        }
      })
    },

    onDialogClose () {
      this.clearCache()
      this.dialog = false
    },

    async onDownloadPhoto ({ blob, id }) {
      this.displayAlert('success', 'Generating photo link to download...')
      this.downloadAttachment(id).
        then(fullResPhoto => {
          downloadURI(URL.createObjectURL(fullResPhoto), blob.filename)
        }).
        catch(e => this.displayAlert('error', parseErrorResponseMessage(e)))
    },

    /**
     * Handles file selection, converting HEIC/HEIF to JPEG before loading the cropper.
     */
    async onFileChange () {
      if (!this.selectedPhoto) {
        this.clearCache()
        return
      }

      this.removingBackground = false
      this.backgroundRemoved = false
      this.pendingCropCoordinates = null
      this.pendingCropImageSize = null

      if (this.image.src && this.image.src.startsWith('blob:')) {
        URL.revokeObjectURL(this.image.src)
      }

      let targetFile = this.selectedPhoto
      const isHeic = targetFile.type === 'image/heic' ||
        targetFile.type === 'image/heif' ||
        /\.(heic|heif)$/i.test(targetFile.name)

      if (isHeic) {
        this.convertingHeic = true
        this.displayAlert('info', 'Optimizing Apple HEIC photo format...')

        try {
          targetFile = await this.convertHeicToJpeg(targetFile)
          this.selectedPhoto = targetFile
        } catch (_) {
          this.convertingHeic = false
          this.displayAlert('error', 'Unable to read this HEIC file. Try opening it in Photos app and exporting as JPEG.')
          this.clearCache()
          return
        }

        this.convertingHeic = false
      }

      const blobUrl = URL.createObjectURL(targetFile)
      const reader = new FileReader()

      reader.onload = e => {
        this.image = {
          src: blobUrl,
          type: getMimeType(e.target.result, targetFile.type),
        }
      }

      reader.readAsArrayBuffer(targetFile)
    },

    onFileDelete () {
      this.deleteAttachment(this.peParty?.photo?.id).
        then(_ => {
          this.displayAlert('success', 'Party Photo Delete Successfully')
          this.peRefreshPhoto()
          this.onDialogClose()
        })
    },

    onFileUpload () {
      const { canvas } = this.$refs.cropper.getResult()
      if (!canvas) return

      this.uploading = true

      // Enforce white flattening context right before dispatching the payload
      const safeCanvas = this.getFlattenedWhiteCanvas(canvas)

      safeCanvas.toBlob(blob => {
        this.peUploadPartyPhoto({
          filename: this.selectedPhoto.name.replace(/\.[^.]+$/, '') + '.jpg',
          photo: blob,
          photoOriginal: this.selectedPhoto,
        }).
          then(_ => {
            this.displayAlert('success', 'Party Photo Uploaded Successfully')
            this.dialog = false
          }).
          catch(e => this.displayAlert('error', parseErrorResponseMessage(e))).
          finally(_ => {
            this.uploading = false
          })
      }, 'image/jpeg', 0.9) // Enforce strict JPEG conversion to dump the Alpha channel cleanly onto white
    },

    async onPrintPhotoToPdf (size) {
      this.displayAlert('success', `Generating PDF (${size})...`)
      try {
        await this.printPhoto({ partyId: this.peParty.id, size })
      } catch (e) {
        this.displayAlert('error', parseErrorResponseMessage(e) || 'Unable to export PDF.')
      }
    },

    async onPrintPhotoToPrinter (size) {
      const sizeDimensions = {
        '4x6': { height: '6in', label: '4" x 6"', width: '4in' },
        '8.5x11': { height: '11in', label: '8.5" x 11"', width: '8.5in' },
      }

      const config = sizeDimensions[size] || sizeDimensions['8.5x11']
      this.displayAlert('success', `Preparing print layout (${config.label})...`)

      try {
        const pdfBlob = await this.printPhoto({
          expectDataOnly: true,
          partyId: this.peParty.id,
          size: size,
        })

        if (!pdfBlob) throw new Error('No data received from server')

        const blobUrl = URL.createObjectURL(pdfBlob)
        const iframe = document.createElement('iframe')

        Object.assign(iframe.style, {
          border: '0',
          bottom: '0',
          height: config.height,
          position: 'fixed',
          right: '0',
          visibility: 'hidden',
          width: config.width,
        })

        iframe.src = blobUrl
        document.body.appendChild(iframe)

        iframe.onload = _ => {
          const style = document.createElement('style')
          style.textContent = `@media print { @page { size: ${config.width} ${config.height}; margin: 0; } }`

          try {
            iframe.contentWindow.document.head.appendChild(style)
          } catch (e) {
            this.displayAlert('error', parseErrorResponseMessage(e))
          }

          iframe.contentWindow.focus()
          iframe.contentWindow.print()

          iframe.contentWindow.addEventListener('afterprint', _ => {
            document.body.removeChild(iframe)
            URL.revokeObjectURL(blobUrl)
          }, { once: true })
        }
      } catch (e) {
        this.displayAlert('error', parseErrorResponseMessage(e) || 'Unable to open printer dialog.')
      }
    },

    async onRemoveBackground () {
      if (!this.selectedPhoto || this.backgroundRemoved) return

      try {
        this.removingBackground = true
        this.caption = 'Initializing engine... 0%'

        // Remember the user's current crop so it can be restored once the
        // background-removed image swaps in below — otherwise the cropper
        // resets to its default full-image box on every src change.
        const currentResult = this.$refs.cropper?.getResult()
        if (currentResult?.coordinates) {
          this.pendingCropCoordinates = { ...currentResult.coordinates }
          this.pendingCropImageSize = currentResult.image ? { ...currentResult.image } : null
        }

        const config = {
          debug: false,
          device: 'gpu',
          output: {
            format: 'image/png',
            quality: 0.8,
          },

          progress: (key, current, total) => {
            const rawPercentage = current / total
            const [type, subtype] = key.split(':')
            let overallPercentage = 0

            if (type === 'fetch') {
              overallPercentage = Math.round(rawPercentage * 35)
              this.caption = `Analyzing... ${overallPercentage}%`
            } else if (type === 'compute') {
              if (subtype !== 'encode') {
                overallPercentage = Math.round(35 + (rawPercentage * 55))
                this.caption = `Processing... ${overallPercentage}%`
              } else {
                overallPercentage = Math.round(90 + (rawPercentage * 10))
                this.caption = `Finalizing... ${overallPercentage}%`
              }
            } else {
              this.caption = 'Processing...'
            }
          },

          rescale: true,
        }

        const resultBlob = await removeBackground(this.selectedPhoto, config)
        const processedFile = new File(
          [resultBlob],
          this.selectedPhoto.name.replace(/\.[^.]+$/, '') + '-no-background.png',
          { type: 'image/png' }
        )

        if (this.image.src && this.image.src.startsWith('blob:')) {
          URL.revokeObjectURL(this.image.src)
        }

        this.selectedPhoto = processedFile
        this.image = {
          src: URL.createObjectURL(processedFile),
          type: 'image/png',
        }

        this.backgroundRemoved = true
        this.displayAlert('success', 'Background removed successfully.')
      } catch (e) {
        // The image src never changed, so @ready won't fire to consume these —
        // clear them here or they'd wrongly apply to some later image load.
        this.pendingCropCoordinates = null
        this.pendingCropImageSize = null
        this.displayAlert('error', parseErrorResponseMessage(e) || 'Unable to remove background.')
      } finally {
        this.removingBackground = false
      }
    },

    setEditingMode (value) {
      this.editing = value
    },

    ...mapActions(PARTY_NAMESPACE, [
      'peRefreshPhoto',
      'peUploadPartyPhoto',
      'printPhoto',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'deleteAttachment',
      'downloadAttachment',
    ]),
  },
}
</script>

<style scoped lang="scss">
.min-h-36 {
  min-height: 36px;
}

.preview-wrapper {
  position: relative;
  width: 400px;
  height: 400px;
  margin: auto;
  border: 1px dashed #ccc;
  border-radius: 8px;
  overflow: hidden;

  .preview-guide,
  .photo-preview {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .preview-guide {
    z-index: 2;
    pointer-events: none;
  }
}

::v-deep .custom-handler {
  background: #ff0000 !important;
  border: 1px solid #ffffff !important;
}

::v-deep .custom-line {
  border-color: #0000ff !important;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 1.8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.cropper-container {
  max-height: 400px;
  min-height: 400px;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #ddd;
  position: relative;
  /* Hard block transparency elements from ever falling back to dark themes */
  background: #FFFFFF !important;
}

.cropper {
  max-height: 400px;
  min-height: 400px;
  width: 100%;
  background-color: var(--cropper-current-bg, #FFFFFF) !important;
  transition: background-color 0.2s ease-in-out;

  ::v-deep .vue-advanced-cropper__background,
  ::v-deep .vue-advanced-cropper__foreground {
    background: var(--cropper-current-bg, #FFFFFF) !important;
  }
}

.has-transparent-bg {
  .cropper {
    --cropper-current-bg: #FFFFFF !important;
  }
}
</style>
